(function () {
  const CART_TYPE = document.body.dataset.cartType || 'drawer';

  function getDrawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function getPreloader() {
    return getDrawer()?.querySelector('[data-cart-drawer-preloader]');
  }

  function showPreloader() {
    const preloader = getPreloader();
    if (!preloader) return;
    preloader.hidden = false;
  }

  function hidePreloader() {
    const preloader = getPreloader();
    if (!preloader) return;
    preloader.hidden = true;
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }

    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
    });
  }

  function updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('site-header__cart-count--empty', count === 0);
      el.classList.toggle('xlp-cart-count--empty', count === 0);
    });

    const drawerCount = document.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = count;

    const footer = getDrawer()?.querySelector('[data-cart-drawer-footer]');
    if (footer) footer.hidden = count === 0;
  }

  function updateSubtotal(cents) {
    const subtotal = document.querySelector('[data-cart-drawer-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cents);
  }

  async function fetchSectionHtml(sectionId) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?section_id=${sectionId}`, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('Failed to refresh cart drawer');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('[data-cart-drawer-inner]');
    return inner ? inner.innerHTML : '';
  }

  async function loadRecommendations(container) {
    const drawer = getDrawer();
    const addLabel = drawer?.dataset.addLabel || 'Add';
    const productId = container.dataset.productId;
    const limit = container.dataset.limit || '2';
    const list = container.querySelector('[data-cart-recommendations-list]');
    if (!productId || !list) return;

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`;

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) {
        container.hidden = true;
        return;
      }

      const data = await response.json();
      const products = (data.products || []).filter((product) => product.available !== false);

      if (!products.length) {
        container.hidden = true;
        return;
      }

      list.innerHTML = products
        .map((product) => {
          const variant = product.variants.find((v) => v.available) || product.variants[0];
          if (!variant) return '';

          const image = product.featured_image || '';
          const price = formatMoney(variant.price);

          return `
            <div class="cart-drawer__recommendation">
              <div class="cart-drawer__recommendation-media">
                ${
                  image
                    ? `<img src="${image}" alt="${product.title.replace(/"/g, '&quot;')}" class="cart-drawer__recommendation-image" loading="lazy">`
                    : ''
                }
              </div>
              <div class="cart-drawer__recommendation-details">
                <div class="cart-drawer__recommendation-title">${product.title}</div>
                <div class="cart-drawer__recommendation-price">${price}</div>
              </div>
              <button
                type="button"
                class="cart-drawer__recommendation-add"
                data-cart-add
                data-variant-id="${variant.id}"
              >
                ${addLabel}
              </button>
            </div>
          `;
        })
        .join('');

      container.hidden = false;
    } catch {
      container.hidden = true;
    }
  }

  async function refreshDrawer(openAfterRefresh) {
    const drawer = getDrawer();
    if (!drawer) return;

    const sectionId = drawer.dataset.sectionId;
    const inner = drawer.querySelector('[data-cart-drawer-inner]');
    if (!sectionId || !inner) return;

    inner.innerHTML = await fetchSectionHtml(sectionId);

    const recommendations = inner.querySelector('[data-cart-recommendations]');
    if (recommendations) await loadRecommendations(recommendations);

    if (openAfterRefresh) openDrawer(false);
  }

  function setDrawerActive(isActive) {
    const drawer = getDrawer();
    if (!drawer) return;

    if (isActive) {
      drawer.removeAttribute('inert');
    } else {
      drawer.setAttribute('inert', '');
    }
  }

  function openDrawer(focusClose) {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.add('is-open');
    setDrawerActive(true);
    document.documentElement.classList.add('cart-drawer-open');

    if (focusClose !== false) {
      const closeBtn = drawer.querySelector('.cart-drawer__close');
      closeBtn?.focus();
    }
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.remove('is-open');
    setDrawerActive(false);
    document.documentElement.classList.remove('cart-drawer-open');
  }

  async function changeLine(lineKey, quantity) {
    showPreloader();

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();
      updateCartCounts(cart.item_count);
      updateSubtotal(cart.total_price);
      await refreshDrawer(true);
      return cart;
    } finally {
      hidePreloader();
    }
  }

  async function addToCartWithDrawer(variantId, quantity, options = {}) {
    const { openFirst = true, usePreloader = true } = options;

    if (openFirst) openDrawer(false);
    if (usePreloader) showPreloader();

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const item = await response.json();

      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      updateCartCounts(cart.item_count);
      updateSubtotal(cart.total_price);
      await refreshDrawer(false);

      return item;
    } finally {
      if (usePreloader) hidePreloader();
    }
  }

  function bindDrawerEvents() {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.bound === 'true') return;
    drawer.dataset.bound = 'true';

    drawer.addEventListener('click', async (event) => {
      const openTrigger = event.target.closest('[data-cart-drawer-open]');
      if (openTrigger) {
        event.preventDefault();
        openDrawer();
        return;
      }

      const closeTrigger = event.target.closest('[data-cart-drawer-close]');
      if (closeTrigger) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
      if (quantityTrigger) {
        event.preventDefault();
        quantityTrigger.disabled = true;
        try {
          await changeLine(quantityTrigger.dataset.lineKey, Number(quantityTrigger.dataset.quantity));
        } finally {
          quantityTrigger.disabled = false;
        }
        return;
      }

      const addTrigger = event.target.closest('[data-cart-add]');
      if (addTrigger) {
        event.preventDefault();
        addTrigger.disabled = true;
        try {
          await addToCartWithDrawer(addTrigger.dataset.variantId, 1, { openFirst: false });
        } finally {
          addTrigger.disabled = false;
        }
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    const recommendations = drawer.querySelector('[data-cart-recommendations]');
    if (recommendations) loadRecommendations(recommendations);
  }

  function bindGlobalTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-drawer-open]');
      if (!trigger || CART_TYPE !== 'drawer') return;

      event.preventDefault();
      openDrawer();
    });

    document.addEventListener('submit', async (event) => {
      if (CART_TYPE !== 'drawer') return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('[data-card-product-form]') && !form.matches('[data-main-product-form]')) return;

      const submitter = event.submitter;
      if (submitter && submitter.name === 'checkout') return;

      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;

      event.preventDefault();

      const formData = new FormData(form);
      const variantId = formData.get('id');
      if (!variantId) return;

      const quantity = Number(formData.get('quantity')) || 1;
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      try {
        await addToCartWithDrawer(variantId, quantity, { openFirst: true, usePreloader: true });
      } catch {
        hidePreloader();
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  window.CartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
    add: addToCartWithDrawer,
  };

  function init() {
    if (CART_TYPE !== 'drawer') return;
    bindDrawerEvents();
    bindGlobalTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-cart-drawer]')) {
      const drawer = event.target.querySelector('[data-cart-drawer]');
      if (drawer) drawer.dataset.bound = '';
      bindDrawerEvents();
    }
  });
})();
